import React, { useMemo, useState } from 'react';
import { createStyles, useMantineColorScheme, Box, Text, UnstyledButton, Group, Badge, Checkbox, Divider, Collapse } from '@mantine/core';
import { getColorForKey, getContrastColor, GROUP_COLORS, colorForGroupIndex } from './chartConstants';
import { useCurrency } from '../../utils/helpers';

export const ATTRIBUTES_LABELS = {
    IRR: "Internal Rate Of Return (%)",
    ROI: "Return On Investment (%)",
    ROIDiscounted: "Return On Investment Discounted (%)",
    PaybackTime: "Payback Time (Years)",
    PaybackDiscounted: "Payback Discounted (Years)",
    RenewFraction: "Renew Fraction (%)",
    NPC: "Net Present Cost ({currency})",
    NPCVariation: "Net Present Cost Variation ({currency})",
    AnnCost: "Annual Cost ({currency})",
    AnnCostVariation: "Annual Cost Variation ({currency})",
    LCOE: "Levelized Cost of Energy ({currency}/kWh)",
    LCOS: "Levelized Cost of Storage ({currency}/kWh)",
    OpCost: "Operation Cost ({currency})",
    OpCostVariation: "Operation Cost Variation ({currency})",
    Capital: "Capital ({currency})",
    AutoProd: "Auto - Production (%)",
    AutoCons: "Auto - Consumption (%)",
    PositiveEnergy: "Positive Energy (%)",
    LocalCO2: "Local CO2 (kg)",
    GridCO2: "Grid CO2 (kg)",
    ThermalSupplyCO2: "Thermal Supply CO2 (kg)",
    UnmetEnergyOffgrid: "Unmet Energy Off Grid (kWh)",
    UnmetEnergyGridConnected: "Unmet Energy OnGrid (kWh)",
    ExcessEnergyOffgrid: "Excess Energy OffGrid (kWh)",
    ExcessEnergyGridConnected: "Excess Energy Grid Connected (kWh)",
};

export const METRIC_GROUPS = [
    { id: 'rates', label: 'ECONOMIC PARAMETERS (%)', keys: ['IRR', 'ROI', 'ROIDiscounted', 'RenewFraction', 'AutoProd', 'AutoCons', 'PositiveEnergy'] },
    { id: 'costs', label: 'PROJECT COSTS ({currency})', keys: ['NPC', 'NPCVariation', 'AnnCost', 'AnnCostVariation', 'OpCost', 'OpCostVariation', 'Capital'] },
    { id: 'unit_costs', label: 'LEVELIZED COSTS ({currency}/kWh)', keys: ['LCOE', 'LCOS'] },
    { id: 'time', label: 'PAYBACK (Years)', keys: ['PaybackTime', 'PaybackDiscounted'] },
    { id: 'energy', label: 'ENERGY BALANCE (kWh)', keys: ['UnmetEnergyOffgrid', 'UnmetEnergyGridConnected', 'ExcessEnergyOffgrid', 'ExcessEnergyGridConnected'] },
    { id: 'emissions', label: 'ENVIRONMENTAL (kg CO2)', keys: ['LocalCO2', 'GridCO2', 'ThermalSupplyCO2'] }
];

const useStyles = createStyles((theme) => ({
    container: {
        border: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]}`,
        borderRadius: 4,
        overflow: 'hidden',
    },
    label: {
        fontSize: 14,
        fontWeight: 500,
        marginBottom: 6,
        color: theme.colorScheme === 'dark' ? theme.colors.dark[0] : '#495057',
    },
    groupHeader: {
        width: '100%',
        padding: '10px 14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        cursor: 'pointer',
        transition: 'background-color 0.15s ease',
        borderBottom: `1px solid ${theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[2]}`,
        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
        },
    },
    groupHeaderActive: {
        backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.blue[0],
        borderLeft: `3px solid ${theme.colors.blue[5]}`,
    },
    metricItem: {
        width: '100%',
        padding: '7px 14px 7px 36px',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        cursor: 'pointer',
        transition: 'background-color 0.1s ease',
        '&:hover': {
            backgroundColor: theme.colorScheme === 'dark' ? theme.colors.dark[5] : theme.colors.gray[0],
        },
    },
    chevron: {
        transition: 'transform 0.2s ease',
    },
    chevronOpen: {
        transform: 'rotate(180deg)',
    },
}));

export default function MetricSelection({ value = [], onChange, showSelectedInTrigger = true, label }) {
    const { classes, cx } = useStyles();
    const selectedKeys = value || [];
    const { colorScheme } = useMantineColorScheme();
    const currency = useCurrency();

    // Track which groups are expanded (all collapsed by default)
    const [openGroups, setOpenGroups] = useState(() => {
        const activeGroup = METRIC_GROUPS.find(g => g.keys.some(k => selectedKeys.includes(k)));
        return activeGroup ? { [activeGroup.id]: true } : {};
    });

    const toggleGroup = (groupId) =>
        setOpenGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));

    const getLabel = (key) => (ATTRIBUTES_LABELS[key] || key).replace('{currency}', currency);

    const handleMetricToggle = (key) => {
        const group = METRIC_GROUPS.find(g => g.keys.includes(key));
        const groupIdOfKey = group?.id;
        const activeGroupId = METRIC_GROUPS.find(g => g.keys.some(k => selectedKeys.includes(k)))?.id || null;
        if (activeGroupId && activeGroupId !== groupIdOfKey) {
            onChange([key]);
            return;
        }
        const isSelected = selectedKeys.includes(key);
        const next = isSelected ? selectedKeys.filter(k => k !== key) : [...selectedKeys, key];
        onChange(next);
    };

    const handleGroupToggle = (groupId, groupKeys) => {
        const activeGroupId = METRIC_GROUPS.find(g => g.keys.some(k => selectedKeys.includes(k)))?.id || null;
        if (activeGroupId === groupId && groupKeys.every(k => selectedKeys.includes(k))) {
            // All checked → uncheck all and collapse
            onChange([]);
            setOpenGroups((prev) => ({ ...prev, [groupId]: false }));
            return;
        }
        onChange([...groupKeys]);
    };

    return (
        <Box>
            {label ? (
                <Text className={classes.label}>{label}</Text>
            ) : (
                <Text size="xs" weight={800} color="gray.6" mb={10} transform="uppercase" ml={4} style={{ letterSpacing: '0.5px' }}>
                    Active Data Pool
                </Text>
            )}

            <Box className={classes.container}>
                {METRIC_GROUPS.map((group, groupIdx) => {
                    const groupSelectedCount = group.keys.filter(k => selectedKeys.includes(k)).length;
                    const isAllSelected = groupSelectedCount === group.keys.length;
                    const isIndeterminate = groupSelectedCount > 0 && !isAllSelected;
                    const isOpen = !!openGroups[group.id];
                    const groupColor = (GROUP_COLORS && GROUP_COLORS[group.id]) || null;
                    const hasSelection = groupSelectedCount > 0;

                    return (
                        <Box key={group.id}>
                            {/* ── Group header ── */}
                            <UnstyledButton
                                className={cx(classes.groupHeader, { [classes.groupHeaderActive]: hasSelection })}
                                onClick={() => toggleGroup(group.id)}
                                style={hasSelection && groupColor ? { background: groupColor, color: getContrastColor(groupColor), borderLeftColor: groupColor } : undefined}
                            >
                                <Group spacing="sm" align="center" style={{ flex: 1 }}>
                                    <Checkbox
                                        size="xs"
                                        checked={isAllSelected}
                                        indeterminate={isIndeterminate}
                                        readOnly
                                        styles={{ input: { cursor: 'pointer' } }}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleGroupToggle(group.id, group.keys);
                                        }}
                                    />
                                    <Text weight={700} size="xs" style={{ letterSpacing: '0.4px' }}>
                                        {group.label.replace('{currency}', currency)}
                                    </Text>
                                </Group>
                                <Group spacing={8} align="center">
                                    {groupSelectedCount > 0 && (
                                        <Box sx={{
                                            background: hasSelection && groupColor ? getContrastColor(groupColor) : '#339af0',
                                            color: hasSelection && groupColor ? groupColor : '#fff',
                                            padding: '2px 7px',
                                            borderRadius: 10,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            lineHeight: '18px',
                                        }}>
                                            {groupSelectedCount}
                                        </Box>
                                    )}
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        width="14" height="14"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2.5"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        className={cx(classes.chevron, { [classes.chevronOpen]: isOpen })}
                                    >
                                        <path d="m6 9 6 6 6-6" />
                                    </svg>
                                </Group>
                            </UnstyledButton>

                            {/* ── Metric children ── */}
                            <Collapse in={isOpen}>
                                <Box>
                                    {group.keys.map((key, i) => {
                                        const isSelected = selectedKeys.includes(key);
                                        const color = colorForGroupIndex(i);
                                        return (
                                            <UnstyledButton
                                                key={key}
                                                className={classes.metricItem}
                                                onClick={() => handleMetricToggle(key)}
                                            >
                                                <Box sx={{ width: 10, height: 10, background: color, borderRadius: 2, flexShrink: 0 }} />
                                                <Checkbox
                                                    size="xs"
                                                    checked={isSelected}
                                                    readOnly
                                                    styles={{ input: { cursor: 'pointer' } }}
                                                />
                                                <Text
                                                    size="sm"
                                                    weight={isSelected ? 600 : 400}
                                                    style={isSelected ? { color } : undefined}
                                                >
                                                    {getLabel(key)}
                                                </Text>
                                            </UnstyledButton>
                                        );
                                    })}
                                </Box>
                            </Collapse>

                            {groupIdx < METRIC_GROUPS.length - 1 && !isOpen && (
                                <Divider />
                            )}
                        </Box>
                    );
                })}
            </Box>
        </Box>
    );
}
